import { CartItem, ProductPackage, ProductPackageProduct, SaleItem, SaleTariff } from '@src/core/buyerApiClient';
import { formatDate } from '.';
import { PackageGroup } from './cart';

interface DateBySellingMode {
  item: SaleItem | CartItem;
  i18n: (messageId: any | undefined, query?: { [key: string]: string }) => string;
}

type Item = CartItem | SaleItem;

function isSaleTariff(tariff: any): tariff is SaleTariff {
  return 'unitPriceCents' in tariff;
}

export const getPackageDates = (items: Item[]) => {
  const dates = items.filter((item) => item.scheduleDate).map((item) => formatDate(item.scheduleDate || ''));

  return [...new Set(dates)].join(' - ');
};

export const getFormattedTariffs = (items: (CartItem | SaleItem)[]) => {
  const tariffs = items.map((item) => item.tariffs).flat();

  const tariffGroups = tariffs.reduce((acc, tariff) => {
    const name = isSaleTariff(tariff) ? tariff.name : tariff.tariff.type.name;
    acc[name] = (acc[name] || 0) + tariff.quantity;
    return acc;
  }, {} as Record<string, number>);

  return Object.entries(tariffGroups)
    .map(([name, quantity]) => `${name} (${quantity})`)
    .join(', ');
};

export const calculatePackageTotal = <T extends SaleItem | CartItem>(items: T[], callback: (item: T) => number) => {
  return items.reduce((acc, item) => acc + callback(item), 0);
};

export const getTariffTotalPrice = (tariffs: SaleItem['tariffs']) =>
  tariffs.reduce((total, current) => total + (current.unitPriceCents ?? 0) * current.quantity, 0);

export const getSaleItemDiscountValue = (item: SaleItem) => item.fullPriceCents - item.totalPriceCents;

export function groupItemsByProductPackage<T extends CartItem | SaleItem>(items: T[]) {
  const grouped: Record<string, PackageGroup<T>> = {};
  const nonPackageItems: T[] = [];

  for (const item of items) {
    if (item.productPackageUnitId) {
      const productPackageProduct = item.productPackageProduct as ProductPackageProduct;
      const productPackage = productPackageProduct.productPackage;

      if (!grouped[item.productPackageUnitId]) {
        grouped[item.productPackageUnitId] = {
          productPackage: productPackage!,
          items: [],
        };
      }

      grouped[item.productPackageUnitId]!.items.push(item);
    } else {
      nonPackageItems.push(item);
    }
  }

  return {
    packages: Object.values(grouped),
    nonPackageItems,
  };
}

export const groupSaleItemsByProductPackage = (items: SaleItem[]) => groupItemsByProductPackage(items);
export const groupCartItemsByProductPackage = (items: CartItem[]) => groupItemsByProductPackage(items);

export const dateBySellingMode = ({ item, i18n }: DateBySellingMode) => {
  const { scheduleDate, formattedScheduleTimeMinutes } = item;
  const date = scheduleDate ? `${i18n('checkout_purchase_summary_day')} ${formatDate(scheduleDate)}` : '';

  if (formattedScheduleTimeMinutes) {
    return `${date} - ${formattedScheduleTimeMinutes}`;
  }

  return date || i18n('checkout_purchase_summary_without_date');
};
